"use client"
import React, { useEffect, useState } from 'react';
import { getlog } from "../../utils/API"; // Should return all logs at once
import { FaInfoCircle, FaExclamationTriangle } from "react-icons/fa"

const LogViewer = () => {
  const [rawLogs, setRawLogs] = useState<any[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<any[]>([]);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const logsPerPage = 300;

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const data = await getlog(); // returns { logs: string }

        const parsed = data.logs
          .split('\n')
          .filter((line: any) => line.trim())
          .map((line: any) => {
            try {
              return JSON.parse(line);
            } catch {
              return null;
            }
          })
          .filter(Boolean)
          .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setRawLogs(parsed);
      } catch (error) {
        console.error('Error fetching logs:', error);
        setRawLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  // Update filtered logs when rawLogs or searchText changes
  useEffect(() => {
    const lowerSearch = searchText.toLowerCase();
    const filtered = rawLogs.filter((log: any) =>
      `${log.message} ${log.type} ${log.stack || ''}`.toLowerCase().includes(lowerSearch)
    );
    setFilteredLogs(filtered);
    setPage(1); // Reset to first page on search
  }, [searchText, rawLogs]);

  // Pagination logic
  const startIndex = (page - 1) * logsPerPage;
  const currentLogs = filteredLogs.slice(startIndex, startIndex + logsPerPage);
  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  return (
    <div className="p-2">
      <h2 className="text-lg font-semibold mb-2">Logs Viewer</h2>

      <input
        type="text"
        placeholder="Search logs..."
        value={searchText}
        onChange={handleSearchChange}
        className="border p-1 rounded mb-2 w-full text-sm"
      />

      {loading ? (
        <p className="text-sm">Loading logs...</p>
      ) : currentLogs.length === 0 ? (
        <p className="text-sm">No logs found.</p>
      ) : (
        <div className="border rounded max-h-[500px] overflow-y-auto">
          <table className="min-w-full text-xs table-fixed">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="text-left p-1 border-b w-32">Time</th>
                <th className="text-left p-1 border-b w-20">Type</th>
                <th className="text-left p-1 border-b">Message</th>
              </tr>
            </thead>
            <tbody className="bg-gray-900 text-green-400">
              {currentLogs.map((log, index) => (
                <tr key={index} className="border-b border-gray-700">
                  <td className="p-1 text-gray-400 whitespace-nowrap">{log.timestamp}</td>
                  <td className="p-1 text-white flex items-center gap-1">
                    {log.type === "ERROR" ? (
                      <FaExclamationTriangle className="text-red-400" />
                    ) : (
                      <FaInfoCircle className="text-blue-400" />
                    )}
                    {log.type}
                  </td>
                  <td className="p-1 break-words">{log.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      <div className="mt-2 flex gap-2 items-center text-sm">
        <button
          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
          disabled={page === 1}
          className="bg-gray-700 text-white px-2 py-1 rounded disabled:opacity-50"
        >
          Previous
        </button>

        <span>Page {page} of {totalPages}</span>

        <button
          onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
          disabled={page === totalPages}
          className="bg-gray-700 text-white px-2 py-1 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>

  );
};

export default LogViewer;
